/**
 * useCategories — Fetch active categories with cache and retry.
 * Reduces 429s and "failed to load" when Navbar and other components need categories.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config/api';

const CACHE_TTL_MS = 5 * 60 * 1000;
const TIMEOUT_MS = 30000;
const MAX_RETRIES = 2;

let cached = { data: null, timestamp: 0 };
let inFlight = null;

function getCached() {
  if (!cached.data) return null;
  if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
    cached = { data: null, timestamp: 0 };
    return null;
  }
  return cached.data;
}

/**
 * Shared fetch: cache + retry + timeout. Resolves to array of categories or null.
 */
async function fetchCategoriesShared(signal, attempt = 0) {
  const hit = getCached();
  if (hit) return hit;

  if (inFlight) {
    try {
      return await inFlight;
    } catch {
      return null;
    }
  }

  const promise = (async () => {
    for (let a = attempt; a <= MAX_RETRIES; a++) {
      const timeoutCtrl = new AbortController();
      const timeoutId = setTimeout(() => timeoutCtrl.abort(), TIMEOUT_MS);
      let combinedSignal = timeoutCtrl.signal;
      if (signal) {
        const ac = new AbortController();
        signal.addEventListener('abort', () => ac.abort());
        timeoutCtrl.signal.addEventListener('abort', () => ac.abort());
        combinedSignal = ac.signal;
      }

      try {
        const res = await fetch(`${API_URL}/categories?isActive=true`, {
          cache: 'no-store',
          signal: combinedSignal,
        });
        clearTimeout(timeoutId);

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const list = json.data ?? json.categories;
        const resolved = Array.isArray(list)
          ? list
          : list?.categories && Array.isArray(list.categories)
            ? list.categories
            : [];
        if (resolved.length > 0) {
          cached = { data: resolved, timestamp: Date.now() };
          return resolved;
        }
        return [];
      } catch (err) {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError' || signal?.aborted) throw err;
        if (a < MAX_RETRIES) {
          const delay = Math.min(1000 * Math.pow(2, a), 8000);
          await new Promise((r) => setTimeout(r, delay));
          continue;
        }
        throw err;
      }
    }
    return null;
  })();

  inFlight = promise;
  try {
    return await promise;
  } finally {
    inFlight = null;
  }
}

// (abortSignalAny removed — composition done inside fetchCategoriesShared)

/**
 * Hook: returns { categories, loading, error, refetch }.
 * categories is an array of category objects (with subcategories) or [].
 */
export function useCategories(options = {}) {
  const { enabled = true } = options;
  const [categories, setCategories] = useState(() => (enabled ? getCached() : null) ?? []);
  const [loading, setLoading] = useState(enabled && !getCached());
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchCategories = useCallback(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    setLoading(true);
    setError(null);

    fetchCategoriesShared(signal)
      .then((list) => {
        if (signal.aborted) return;
        const resolved = Array.isArray(list) ? list : [];
        setCategories(resolved);
        setError(null);
      })
      .catch((err) => {
        if (signal.aborted || err.name === 'AbortError') return;
        setError(err);
        setCategories([]);
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const hit = getCached();
    if (hit && hit.length > 0) {
      setCategories(hit);
      setLoading(false);
      return;
    }
    fetchCategories();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [enabled, fetchCategories]);

  const refetch = useCallback(() => {
    cached = { data: null, timestamp: 0 };
    fetchCategories();
  }, [fetchCategories]);

  return { categories, loading, error, refetch };
}
